import { Link } from 'react-router-dom';
import { ArrowLeft, Package, RefreshCw, Mail, Clock, CheckCircle } from 'lucide-react';

export default function ReturnsPage() {
  return (
    <div className="pt-28 pb-16" data-testid="returns-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Our Policies</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Returns & Exchanges
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Your satisfaction is our priority. We want you to love your Whispering Wristwear piece.
          </p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <Clock className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">30-Day Window</h3>
            <p className="text-sm text-[#52796F]">Return within 30 days of delivery</p>
          </div>
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <RefreshCw className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">Free Exchanges</h3>
            <p className="text-sm text-[#52796F]">Exchange for a different size or style</p>
          </div>
          <div className="bg-[#F5F5F0] p-6 rounded-sm text-center">
            <Package className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-lg text-[#354F52] mb-2">Easy Process</h3>
            <p className="text-sm text-[#52796F]">Simple steps to start your return</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-10">
          {/* Return Policy */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">1</span>
              Return Policy
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                We want you to be completely happy with your purchase. If for any reason you're not satisfied, 
                you may return your item within <strong className="text-[#354F52]">30 days of delivery</strong> for 
                a full refund or exchange.
              </p>
              <div className="bg-[#F5F5F0] p-4 rounded-sm">
                <p className="font-medium text-[#354F52] mb-2">To be eligible for a return:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span>Item must be unworn and in original condition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span>Original packaging must be included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#52796F] mt-1 flex-shrink-0" strokeWidth={1.5} />
                    <span>Meaning card and any accessories must be returned</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Custom Orders */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">2</span>
              Custom Orders
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                Because custom orders are made specifically for you, they are <strong className="text-[#354F52]">final sale</strong> and 
                cannot be returned or exchanged unless there is a defect or error on our part.
              </p>
              <p>
                We take great care to ensure your custom piece is exactly as ordered. Before we begin crafting, 
                we'll send you a confirmation of your Morse code message for approval.
              </p>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 rounded-sm">
                <p className="text-[#354F52]">
                  <strong>Note:</strong> If we make an error in your custom order (wrong letters, incorrect spelling), 
                  we will remake it at no charge or provide a full refund.
                </p>
              </div>
            </div>
          </section>

          {/* Exchanges */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">3</span>
              Exchanges
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                Want to swap your bracelet for a different style or message? We're happy to help! 
                Exchanges are <strong className="text-[#354F52]">free for standard collection items</strong>.
              </p>
              <p>
                Simply contact us within 30 days of receiving your order, and we'll guide you through the exchange process. 
                If there's a price difference, we'll either charge or refund the difference.
              </p>
            </div>
          </section>

          {/* How to Return */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">4</span>
              How to Start a Return
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <ol className="space-y-4">
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-[#84A98C] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                  <div>
                    <p className="font-medium text-[#354F52]">Contact Us</p>
                    <p>Email us at <a href="mailto:returns@whisperingwristwear.com" className="text-[#D4AF37] hover:underline">returns@whisperingwristwear.com</a> with 
                    your order number and reason for return.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-[#84A98C] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                  <div>
                    <p className="font-medium text-[#354F52]">Receive Return Label</p>
                    <p>We'll email you a prepaid return shipping label within 1-2 business days.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-[#84A98C] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                  <div>
                    <p className="font-medium text-[#354F52]">Ship Your Item</p>
                    <p>Pack your item securely in its original packaging and drop it off at any postal location.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-6 h-6 bg-[#84A98C] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                  <div>
                    <p className="font-medium text-[#354F52]">Receive Your Refund</p>
                    <p>Once we receive and inspect your return, we'll process your refund within 5-7 business days.</p>
                  </div>
                </li>
              </ol>
            </div>
          </section>

          {/* Damaged Items */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">5</span>
              Damaged or Defective Items
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                If your item arrives damaged or has a manufacturing defect, please contact us immediately at{' '}
                <a href="mailto:support@whisperingwristwear.com" className="text-[#D4AF37] hover:underline">support@whisperingwristwear.com</a>.
              </p>
              <p>
                Include photos of the damage and your order number. We'll send a replacement or issue a full refund 
                at no additional cost to you.
              </p>
            </div>
          </section>

          {/* Refund Timeline */}
          <section>
            <h2 className="font-serif text-2xl text-[#354F52] mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#354F52] text-[#FAF9F6] rounded-full flex items-center justify-center text-sm">6</span>
              Refund Timeline
            </h2>
            <div className="pl-11 space-y-4 text-[#52796F] leading-relaxed">
              <p>
                Refunds are processed to your original payment method within <strong className="text-[#354F52]">5-7 business days</strong> after 
                we receive your return.
              </p>
              <p>
                Please note that your bank or credit card company may take an additional 5-10 business days 
                to post the refund to your account.
              </p>
            </div>
          </section>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 p-8 bg-[#354F52] rounded-sm text-center text-[#FAF9F6]">
          <Mail className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl mb-3">Questions?</h3>
          <p className="text-[#CAD2C5] mb-6 max-w-md mx-auto">
            Our team is here to help. Reach out and we'll get back to you within 24 hours.
          </p>
          <a 
            href="mailto:support@whisperingwristwear.com" 
            className="inline-block bg-[#D4AF37] hover:bg-[#B8972E] text-[#354F52] px-8 py-3 rounded-full font-medium uppercase tracking-widest text-xs transition-colors"
            data-testid="contact-support-btn"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}
